<template>
<tr v-if="Object.hasOwn(row, 'parsed')" class="cte-row cte-raw-toggle" :class="rowStatusClass"
  @click="toggleExpanded"
>

  <!-- expand button and date -->
<td class="cte-date-col ">
  <button v-if="!row.isNotFound && hasRaw" type="button" class="">
    <span class="cte-caret" :class="{ 'cte-caret-open': expanded }">&#9656;</span>
  </button>
  <span v-tippy="{ content: fullTimestamp, placement: 'top' }">{{ shortTimestamp }}</span>
  <!-- only when this event was scraped at a terminal other than the container's current discharge terminal -->
  <div v-if="hasTerminalDifferentFromDischarge" class="cte-event-terminal">
    <span v-tippy="{ content: 'Scraped at ' + terminalType, placement: 'top' }" class="uk-label cte-compact-label">{{ terminalType }}</span>
  </div>
</td>

<td v-if="row.isNotFound" colspan="9">Not Found</td>

<!-- container (available) status -->
<td class="cte-condensed-col " v-if="!row.isNotFound">
  <span v-if="row.isPickedUp" class="uk-label tw-bg-slate-500">n/a</span>
  <span v-else-if="row.isAvailable" class="uk-label uk-label-success">Yes</span>
  <span v-else class="uk-label uk-label-danger">No</span>
</td>

<!-- container location (aka yard status) -->
<td class="cte-location-col " v-if="!row.isNotFound">
  <span v-tippy="parsed['container_location']">
    <span v-if="row.isPickedUp" class="uk-label uk-label-success" 
      :class="[hasFieldChangedClass('container_location')]">Off-dock</span>
    <span v-else-if="row.isAvailable || row.isInYard" class="uk-label uk-label-success" 
      :class="[hasFieldChangedClass('container_location')]">In Yard</span>
      <span v-else-if="!row.isOffVessel" class="uk-label" 
      :class="[hasFieldChangedClass('container_location')]">On Vessel</span>
    <span v-else class="uk-label" 
      :class="[hasFieldChangedClass('container_location')]">{{ parsed['container_location'] }}
    </span>
  </span>
</td>

<!-- Customs holds -->
<td class="cte-condensed-col " v-if="!row.isNotFound">
  <span 
    v-if="parsed['holds']?.['customs']" 
    class="uk-label"
    :class="[ hasFieldChangedClass('holds', 'customs'), (parsed['holds']['customs']['active'] ? 'uk-label-danger' : 'uk-label-success') ]"> 
    <span v-tippy="getParsedHoldDetailsWithEmptyPlaceholder('customs')">{{ parsed['holds']['customs']['status'].toUpperCase() }}</span>
  </span>
  <span v-else class="uk-label uk-label-success" :class="[hasFieldChangedClass('holds', 'customs')]">-</span>
</td>

<!-- Line holds -->
<td class="cte-condensed-col " v-if="!row.isNotFound">
  <span 
    v-if="parsed['holds']?.['line']" 
    class="uk-label" 
    :class="[ hasFieldChangedClass('holds', 'line'), (parsed['holds']['line']['active'] ? 'uk-label-danger' : 'uk-label-success') ]"> 
    <span v-tippy="getParsedHoldDetailsWithEmptyPlaceholder('line')">{{ parsed['holds']['line']['status'].toUpperCase() }}</span>
    

  </span>
  <span v-else class="uk-label uk-label-success" :class="[hasFieldChangedClass('holds', 'line')]">-</span>
</td>

<!-- Terminal Holds -->
<td class="cte-condensed-col " v-if="!row.isNotFound">
  <span 
    v-if="parsed['holds']?.['terminal']" 
    class="uk-label" 
    :class="[ hasFieldChangedClass('holds', 'terminal'), (parsed['holds']['terminal']['active'] ? 'uk-label-danger' : 'uk-label-success') ]"> 
    <span v-tippy="getParsedHoldDetailsWithEmptyPlaceholder('terminal')">{{ parsed['holds']['terminal']['status'].toUpperCase() }}</span>

  </span>
  <span v-else class="uk-label uk-label-success" :class="[hasFieldChangedClass('holds', 'terminal')]">-</span>
</td>

<!-- USDA Holds -->
<td class="cte-condensed-col " v-if="!row.isNotFound">
  <span 
    v-if="parsed['holds']?.['usda']" 
    class="uk-label" 
    :class="[ hasFieldChangedClass('holds', 'usda'), (parsed['holds']['usda']['active'] ? 'uk-label-danger' : 'uk-label-success') ]"> 
    <span v-tippy="getParsedHoldDetailsWithEmptyPlaceholder('usda')">{{ parsed['holds']['usda']['status'].toUpperCase() }}</span>
  </span>
  <span v-else class="uk-label uk-label-success" :class="[hasFieldChangedClass('holds', 'usda')]">-</span>
</td>

<!-- Other Holds -->
<td class="cte-condensed-col " v-if="!row.isNotFound">
  <span 
    v-if="parsed['holds']?.['other']" 
    class="uk-label" 
    :class="[ hasFieldChangedClass('holds', 'other'), (parsed['holds']['other']['active'] ? 'uk-label-danger' : 'uk-label-success') ]"> 
    <span v-tippy="getParsedHoldDetailsWithEmptyPlaceholder('other')">{{ parsed['holds']['other']['status'].toUpperCase() }}</span>
  </span>
  <span v-else class="uk-label uk-label-success" :class="[hasFieldChangedClass('holds', 'other')]">-</span>
</td>

<!-- Fees -->
<td class="cte-condensed-col " v-if="!row.isNotFound">
  <span v-if="parsed['demurrage_amount'] && parseFloat(parsed['demurrage_amount']) > 0" 
    class="uk-label uk-label-warning" :class="[hasFieldChangedClass('demurrage_amount')]">
    ${{ parsed['demurrage_amount'] }}
  </span>
  <span v-else class="uk-label uk-label-success" :class="[hasFieldChangedClass('demurrage_amount')]">&mdash;</span>
</td>

<!-- Last Free Day -->
<td class="" v-if="!row.isNotFound">
  <span class="" :class="[hasFieldChangedClass('last_free_day')]">
    {{ lastFreeDay }}
  </span>
</td>


</tr>
<!-- Extra details if expanded -->
 <tr v-if="!isNotFound && expanded && hasRaw" class="cte-raw-row">
    <td colspan="10">
      <div class="cte-raw-panel">
        <div class="cte-raw-title">Original terminal data</div>

        <div v-if="terminalType === 'TRAPAC'" class="cte-native">
          <table class="cte-native-table">
            <thead>
              <tr>
                <th></th>
                <th></th>
                <th></th>
                <th colspan="4">Holds</th>
                <th></th>
                <th colspan="3">Dimensions</th>
                <th></th>
                <th></th>
              </tr>
              <tr>
                <th>Number</th>
                <th>Terminal ID</th>
                <th>Shipline ID</th>
                <th>Ship Line</th>
                <th>Customs</th>
                <th>USDA</th>
                <th>Terminal Hold (and Details)</th>
                <th>Yard Status</th>
                <th>Size</th>
                <th>Type</th>
                <th>Height</th>
                <th>Vessel / Voyage</th>
                <th>LFD</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ containerNumber }}</td>
                <td>{{ raw.terminal_id || '' }}</td>
                <td>{{ raw.line_id || '' }}</td>
                <td>{{ raw.holdsLine || '' }}</td>
                <td>{{ raw.holdsCustoms || '' }}</td>
                <td>{{ raw.holdsUSDA || '' }}</td>
                <td>
                  <div 
                    v-for="(holdString, holdIdx) in (raw.terminalHold || [])" 
                    :style="{ borderBottom: Number(holdIdx) < (raw.terminalHold || []).length - 1 ? '1px solid #ccc' : 'none' }"
                    :key="holdString+'-'+holdIdx"
                  >
                    {{ holdString }}
                  </div>
                </td>
                <td>{{ raw.yardStatus || '' }}</td>
                <td>{{ raw.size || '' }}</td>
                <td>{{ raw.cargoType || '' }}</td>
                <td>{{ raw.height || '' }}</td>
                <td>{{ raw.vessel || '' }}/{{ raw.voyage || '' }}</td>
                <td>{{ raw.lfd || '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="terminalType === 'OICT'" class="cte-native">
          <table class="cte-native-table">
            <thead>
              <tr>
                <th>Container</th>
                <th>Available for pick-up?</th>
                <th>Size/Type</th>
                <th>Holds/Fees</th>
                <th>Additional Information</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ containerNumber }}</td>
                <td>
                  <div>{{ raw.container_available || '' }}</div>
                  <small>{{ raw.container_available_extra_info || '' }}</small>
                </td>
                <td>
                  <strong>{{ raw.size_type || '' }}</strong><br>
                  <small>{{ raw.size_type_long || '' }}</small>
                </td>
                <td>
                  <div>Customs Release Status: <strong>{{ raw.customs_release || '' }} <span v-if="raw.customs_release_datetime"><br/>(date: {{ raw.customs_release_datetime || '' }})</span></strong></div>
                  <div>Line Release Status: <strong>{{ raw.line_release || '' }} <span v-if="raw.line_release_datetime"><br/>(date: {{ raw.line_release_datetime || '' }})</span></strong></div>
                  <div>Holds: <strong>{{ raw.holds || '' }}</strong></div>
                  <div>Add'l Holds Info: <strong>{{ raw.additional_holds_info || '' }}</strong></div>
                  <div>Total Fees: <strong>{{ raw.total_fees || '' }}</strong></div>
                  <div>Satisfied Thru: <strong>{{ raw.last_free_day || '' }}</strong></div>
                </td>
                <td>
                  <div>Location: <strong>{{ raw.yard_location || '' }}</strong></div>
                  <div>Ves/Voy: <strong>{{ raw.vessel || '' }}</strong></div>
                  <div>Line: <strong>{{ raw.line_id || '' }}</strong></div>
                  <div>Req. Acc.: <strong>{{ raw.required_accessory || '' }}</strong></div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="terminalType === 'ETS'" class="cte-native">
          <table class="cte-native-table">
            <thead>
              <tr>
                <td>Container:</td>
                <td>Available</td>
                <td>Size</td>
                <td>Height</td>
                <td>Type</td>
                <td>Customs Status</td>
                <td>Carrier Status</td>
                <td>Terminal Hold</td>
                <td colspan="3">Demurrage </td>
                <td>Appointment</td>
                <td>Yard Location</td>
                <td>Wheeled/Decked</td>
              </tr>
              <tr>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td>Status</td>
                <td>Amount</td>
                <td>Good Through (LFD)</td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ containerNumber }}</td>
                <td>{{ raw.available || '' }}</td>
                <td>{{ raw.container_size || '' }}</td>
                <td>{{ raw.container_height || '' }}</td>
                <td>{{ raw.container_type || '' }}</td>
                <td>{{ raw.customs_hold || '' }}</td>
                <td>{{ raw.line_hold || '' }}</td>
                <td>{{ raw.terminal_hold || '' }}</td>
                <td>{{ raw.demurrage_status || '' }}</td>
                <td>{{ raw.demurrage_amount || '' }}</td>
                <td>{{ raw.last_free_day || '' }}</td>
                <td>{{ raw.appointment_datetime || '' }}</td>
                <td>{{ raw.container_location || '' }}</td>
                <td>{{ raw.wheeled_decked || '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </td>
  </tr>
</template>


<script lang="ts">
import { defineComponent } from 'vue';
import moment from 'moment';

export default defineComponent({
  name: 'ContainerTerminalEventsTableRowV2',
  props: {
    row: {
      type: Object,
      required: true,
    },
    terminal: {
      type: String,
      required: true,
    },
    previousRow: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      expanded: false
    }
  },
  methods: {
    toggleExpanded() {
      this.expanded = !this.expanded
    },
    normalizeValue(value: any): string {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    },
    hasFieldChanged(field: string): boolean {
      const vm = this as any;
      if (!vm.parsed || !vm.parsedPrevious) return false;

      return vm.normalizeValue(vm.parsed[field]) !== vm.normalizeValue(vm.parsedPrevious[field]);
    },
    hasHoldFieldChanged(type: string): boolean {
      const vm = this as any;
      const current = vm.parsed?.['holds'];
      const prev = vm.parsedPrevious?.['holds'];
      if (!current || !prev || !current[type] || !prev[type]) return false;

      if (current[type].active !== prev[type].active) {
        return true;
      }
      if (vm.normalizeValue(current[type].status) !== vm.normalizeValue(prev[type].status)) {
        return true;
      }
      const currentDetails = [...(current[type]?.details || [])].sort().join(';;;');
      const prevDetails = [...(prev[type]?.details || [])].sort().join(';;;');
      return vm.normalizeValue(currentDetails) !== vm.normalizeValue(prevDetails);
    },
    hasFieldChangedClass(field: string, holdType: string = ''): string {
      if (field === 'holds' && holdType) {
        return this.hasHoldFieldChanged(holdType) ? 'cte-changed' : '';
      }
      return this.hasFieldChanged(field) ? 'cte-changed' : '';
    },
    getParsedHoldDetailsWithEmptyPlaceholder(holdType) {
      // get details array, remove duplicates via Set, then back to Array and add "empty" label
      return [...new Set(this.parsed?.['holds']?.[holdType]?.['details'] || [])].map(t => t ? t : '(empty)').join(', ');
    },
  },
  computed: {
    isNotFound() {
      return !!this.row.isNotFound;
    },
    rowStatusClass() {
      if (this.row.isPickedUp || this.row.isAvailable) {
        return 'cte-row-positive';
      } else if (this.row.hasHolds || !this.row.isOffVessel) {
        return 'cte-row-negative';
      } else{
        return '';
      }
    },
    shortTimestamp() {
      if (!this.row.dateTimestamp) {
        return this.row.date;
      }
      return moment.unix(this.row.dateTimestamp).format('MM/DD HH:mm');
    },
    fullTimestamp() {
      if (!this.row.dateTimestamp) {
        return this.row.date;
      }
      const date = new Date(this.row.dateTimestamp * 1000);
      const options: Intl.DateTimeFormatOptions = {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZoneName: 'short',
      };
      const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
      const get = (type: string) => {
        const part = parts.find((p) => p.type === type);
        return part ? part.value : '';
      };
      return `${get('year')}/${get('month')}/${get('day')} ${get('hour')}:${get('minute')}:${get('second')} ${get('timeZoneName')}`;
    },
    parsed() {
      if (typeof this.row.parsed === 'string') {
        return JSON.parse(this.row.parsed);
      }
      // fallback for events that have not been updated to the v2 format
      return JSON.parse(this.row.event);
    },
    parsedPrevious() {
      if (typeof this.previousRow?.parsed === 'string') {
        return JSON.parse(this.previousRow.parsed);
      }
      // fallback for
      return null;
    },
    raw() {
      if (typeof this.row.raw === 'string') {
        return JSON.parse(this.row.raw);
      }
      if (this.row.raw && typeof this.row.raw === 'object') {
        return this.row.raw;
      }
      return {};
    },
    hasRaw() {
      return Object.keys(this.raw).length > 0;
    },
    terminalType() {
      return (this.row.terminal || '').toUpperCase();
    },
    hasTerminalDifferentFromDischarge() {
      const dischargeTerminal = (this.terminal || '').trim().toUpperCase();
      return !!this.terminalType && !!dischargeTerminal && this.terminalType !== dischargeTerminal;
    },
    containerNumber() {
      return this.raw.container || this.raw.container_id || this.raw.containerId || this.row.containerId || '';
    },
    lastFreeDay() {
      if (this.row.lastFreeDay) {
        // stored in YYYY-MM-DDTHH:mm:ss format
        // output only the date part (MM-DD)
        const date = this.row?.lastFreeDay?.split('T')?.[0] || ''; 
        if (date.includes('-') && date.match(/^\d\d\d\d-\d\d-\d\d/)) {
          return date.replace(/^(\d\d\d\d)-(\d\d)-(\d\d)/, '$2/$3/$1');

        }
        return date;
        }
      return '';
    },
    sortedRaw() {
      const containerInfo = {};
      const statusInfo = {};
      const holdInfo = {};
      const otherInfo = {};
      const containerLabels = ["container_size", "container_height", "container_type", "size_type", "size_type_long", "vessel", "cargoType", "height", "size"];
      const statusLabels = ["available", "last_free_day", "container_location", "appointment_datetime", "container_available", "container_available_extra_info", "yard_location", "lfd", "yardStatus", "yardDescription"];
      const holdLabels = ["customs_hold", "demurrage_status", "demurrage_rate", "demurrage_amount", "customs_release", "customs_release_datetime", "line_release", "line_release_datetime", "holds", "additional_holds_info", "total_fees", "holdsLine", "holdsUSDA", "holdCustoms", "hold", "terminalHold", "amount"];
      for (const key in this.raw) {
        if (containerLabels.includes(key)) {
          containerInfo[key] = this.raw[key];
        } else if (statusLabels.includes(key)) {
          statusInfo[key] = this.raw[key];
        } else if (holdLabels.includes(key)) {
          holdInfo[key] = this.raw[key];
        } else {
          otherInfo[key] = this.raw[key];
        }
      }
      return { containerInfo, statusInfo, holdInfo, otherInfo };
    }
  }
});
</script>



<style scoped>
 .cte-table :deep(th) {
  white-space: nowrap;
 }

 .cte-empty {
  padding: 8px 0;
 }

 .cte-date {
  display: flex;
  align-items: center;
  gap: 6px;
 }

 .cte-collapse-icon {
  display: inline-flex;
  align-items: center;
  color: rgba(100, 116, 139, 0.95);
 }

 .cte-row :deep(td) {
  vertical-align: top;
 }

.cte-row.cte-row-positive :deep(td) {
  background-color: #eaf7ea !important;
}

.cte-row.cte-row-negative :deep(td) {
  background-color: #fdecec !important;
}

.cte-row.cte-row-positive:hover :deep(td) {
  background-color: #c3ebc3 !important;
  cursor: zoom-in;  
}
.cte-row.cte-row-negative:hover :deep(td) {
  background-color: #f6d9d9 !important;
  cursor: zoom-in;  
}

.cte-row.cte-row-terminal-ets :deep(td) {
  background-color: #b6db9d !important;
}

.cte-row.cte-row-terminal-trapac :deep(td) {
  background-color: #A9C2F0 !important;
}

.uk-label.cte-changed {
  outline: 2px solid #0f172a;
  outline-offset: 1px;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.85);
}

.cte-changed {
  font-weight: 600;
  text-decoration: underline dotted #0f172a;
  text-decoration-thickness: 2px;
  text-underline-offset: 2px;
}

.cte-raw-toggle {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 4px;
  color: #475569;
  line-height: 1;
}

.cte-caret {
  display: inline-block;
  transition: transform 0.15s ease;
}

.cte-caret-open {
  transform: rotate(90deg);
}

.cte-raw-row :deep(td) {
  background: #f8fafc !important;
  border-top: none;
}

.cte-raw-panel {
  padding: 8px 10px 12px;
  
  width:90%;
  overflow: scroll;
}

.cte-raw-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 6px;
}

.cte-native {
  overflow-x: auto;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.cte-native-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.cte-native-table th,
.cte-native-table td {
  border: 1px solid #e2e8f0;
  padding: 4px 8px;
  text-align: left;
  vertical-align: top;
}

.cte-native-table thead th,
.cte-native-table thead td {
  background: #f1f5f9;
  font-weight: 600;
  color: #334155;
}

.cte-native-table a {
  color: #2563eb;
  text-decoration: underline;
}

.cte-native-cb {
  text-align: center;
}

.cte-chip {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  border-radius: 10px;
  padding: 1px 8px;
  margin: 0 3px 3px 0;
  border: 1px solid transparent;
}

.cte-chip-on {
  background: #dcfce7;
  color: #166534;
  border-color: #bbf7d0;
}
.cte-chip-red {
  background: #fee2e2;
  color: #991b1b;
  border-color: #fecaca;
}

.cte-chip-off {
  background: #f1f5f9;
  color: #475569;
  border-color: #e2e8f0;
}

.cte-date-col {
  width: 140px;
 }

 .cte-location-col {
  width: 180px;
 }

.cte-condensed-col {
  width: 10px;
  white-space: normal;
}

.cte-condensed-col .uk-label {
  white-space: normal;
}

.cte-event-terminal {
  margin-top: 2px;
}

.cte-compact-label {
  padding: 2px 6px;
  font-size: 11px;
  white-space: nowrap;
}
</style>
